import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, MapPin, Phone, Mail, Send, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { SITE } from "@/lib/site-data";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_OPTIONS = [
  "Weekly lawn maintenance",
  "Mulch & flower beds",
  "Shrub & tree trimming",
  "Yard cleanup",
  "Landscape design",
  "Something else",
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.06, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [errors, setErrors] = useState({});

  const update = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: null }));
    if (submitError) setSubmitError("");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Tell us your name";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Add a valid email";
    if (!form.phone.trim()) e.phone = "Add a phone we can call you on";
    if (!form.service) e.service = "Pick what you'd like done";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "A few words about the project, please";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await axios.post(`${API_BASE}/contact`, form);
      setSubmitted(true);
      setTimeout(() => {
        const el = document.getElementById("contact");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong sending your request. Please try again or call us.";
      setSubmitError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", service: "", message: "" });
    setErrors({});
    setSubmitError("");
    setSubmitted(false);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#1A2E20] text-white overflow-hidden"
    >
      {/* Decorative blobs */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#2E5C3E] blur-3xl opacity-60"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#EAB308] blur-3xl opacity-15"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          className="lg:col-span-5"
        >
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase font-bold text-[#EAB308] mb-5">
            <MapPin className="w-3.5 h-3.5" />
            Tallahassee, FL · Leon County
          </div>
          <h2 className="font-['Outfit'] text-4xl md:text-6xl font-black tracking-tighter leading-[1.02]">
            Let's make your yard the one neighbors talk about.
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed max-w-md">
            Send us a few details and we'll have a quote in your inbox within
            24 hours — usually faster. Or skip the form and just call us.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`tel:${SITE.phoneTel}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
            >
              <div className="w-11 h-11 rounded-xl bg-[#EAB308] text-[#422006] grid place-items-center">
                <Phone className="w-5 h-5" strokeWidth={2.4} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">
                  Call now
                </div>
                <div className="font-['Outfit'] text-2xl font-bold tracking-tight">
                  {SITE.phone}
                </div>
              </div>
              <ArrowRight className="ml-auto w-5 h-5 text-white/40 group-hover:translate-x-1 group-hover:text-white transition" />
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
            >
              <div className="w-11 h-11 rounded-xl bg-[#2E5C3E] text-white grid place-items-center">
                <Mail className="w-5 h-5" strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">
                  Email
                </div>
                <div className="font-['Outfit'] text-lg font-bold tracking-tight truncate">
                  {SITE.email}
                </div>
              </div>
              <ArrowRight className="ml-auto w-5 h-5 text-white/40 group-hover:translate-x-1 group-hover:text-white transition" />
            </a>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={1}
          className="lg:col-span-7"
        >
          <div className="rounded-[2rem] bg-[#F8FAF8] text-[#1A2E20] p-7 md:p-10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] relative overflow-hidden">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="thanks"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  data-testid="contact-thank-you"
                  className="text-center py-10"
                >
                  <div className="mx-auto w-20 h-20 rounded-full bg-[#E5EDE7] grid place-items-center mb-6">
                    <CheckCircle2
                      className="w-10 h-10 text-[#2E5C3E]"
                      strokeWidth={2.2}
                    />
                  </div>
                  <h3 className="font-['Outfit'] text-3xl md:text-4xl font-black tracking-tighter text-[#1A2E20]">
                    Thanks, {form.name.split(" ")[0] || "neighbor"}!
                  </h3>
                  <p className="mt-4 text-base md:text-lg text-[#4A6E55] leading-relaxed max-w-md mx-auto">
                    We got your message and we'll be in touch within{" "}
                    <span className="font-bold text-[#1A2E20]">24 hours</span>.
                    In the meantime, the kettle's on — feel free to call us at{" "}
                    <a
                      href={`tel:${SITE.phoneTel}`}
                      className="font-bold text-[#2E5C3E] underline underline-offset-4"
                    >
                      {SITE.phone}
                    </a>
                    .
                  </p>
                  <Button
                    data-testid="contact-send-another-btn"
                    onClick={reset}
                    className="mt-8 bg-[#2E5C3E] text-white hover:bg-[#1B3624] rounded-full px-7 h-12 font-bold"
                  >
                    Send another message
                  </Button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="grid gap-5"
                  noValidate
                >
                  <div className="text-xs tracking-[0.28em] uppercase font-bold text-[#2E5C3E]">
                    Free quote · 24-hour reply
                  </div>
                  <h3 className="font-['Outfit'] text-3xl md:text-4xl font-black tracking-tighter text-[#1A2E20] leading-tight">
                    Tell us about your yard.
                  </h3>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="name" className="text-xs font-bold tracking-wider uppercase text-[#4A6E55]">
                        Name
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Jordan Smith"
                        className="h-12 rounded-xl bg-white border-[#D1DED5] focus-visible:ring-[#2E5C3E]"
                      />
                      {errors.name && (
                        <span className="text-xs font-medium text-red-600">
                          {errors.name}
                        </span>
                      )}
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="phone" className="text-xs font-bold tracking-wider uppercase text-[#4A6E55]">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="(850) 555-0123"
                        className="h-12 rounded-xl bg-white border-[#D1DED5] focus-visible:ring-[#2E5C3E]"
                      />
                      {errors.phone && (
                        <span className="text-xs font-medium text-red-600">
                          {errors.phone}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="email" className="text-xs font-bold tracking-wider uppercase text-[#4A6E55]">
                        Email
                      </Label>
                      <Input
                        id="email"
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@email.com"
                        className="h-12 rounded-xl bg-white border-[#D1DED5] focus-visible:ring-[#2E5C3E]"
                      />
                      {errors.email && (
                        <span className="text-xs font-medium text-red-600">
                          {errors.email}
                        </span>
                      )}
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-xs font-bold tracking-wider uppercase text-[#4A6E55]">
                        Service
                      </Label>
                      <Select
                        value={form.service}
                        onValueChange={update("service")}
                      >
                        <SelectTrigger
                          data-testid="contact-select-service"
                          className="h-12 rounded-xl bg-white border-[#D1DED5] focus:ring-[#2E5C3E]"
                        >
                          <SelectValue placeholder="What can we do for you?" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES_OPTIONS.map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              data-testid={`contact-service-option-${s
                                .toLowerCase()
                                .replace(/\s+/g, "-")}`}
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.service && (
                        <span className="text-xs font-medium text-red-600">
                          {errors.service}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="message" className="text-xs font-bold tracking-wider uppercase text-[#4A6E55]">
                      Project details
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Front yard's been neglected, beds are overgrown, and the shrubs need a serious haircut..."
                      className="rounded-xl bg-white border-[#D1DED5] focus-visible:ring-[#2E5C3E] resize-none"
                    />
                    {errors.message && (
                      <span className="text-xs font-medium text-red-600">
                        {errors.message}
                      </span>
                    )}
                  </div>

                  <Button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="mt-2 group bg-[#EAB308] text-[#422006] hover:bg-[#FACC15] font-bold rounded-full h-14 text-base shadow-[0_18px_40px_-18px_rgba(234,179,8,0.85)] transition-transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {submitting ? (
                      <>
                        Sending…
                        <Loader2 className="ml-2 w-4 h-4 animate-spin" />
                      </>
                    ) : (
                      <>
                        Send my free quote request
                        <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                  {submitError && (
                    <div
                      data-testid="contact-submit-error"
                      role="alert"
                      className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
                    >
                      {submitError}
                    </div>
                  )}
                  <p className="text-xs text-[#4A6E55] text-center">
                    No spam, no robo-calls. Just a friendly quote.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
